// Movement utilities for token drag-to-move
// Ported from packages/maps/src/utils/MovementUtils.ts, GeometryUtils.ts, IlluminationUtils.ts

/**
 * @typedef {import('../types/jsdoc-types.js').Wall} Wall
 * @typedef {import('../types/jsdoc-types.js').Opening} Opening
 * @typedef {import('../types/jsdoc-types.js').Token} Token
 */

import { gridPointToPixel } from '../rendering/coordinate-transforms.js';

const FEET_PER_GRID_SQUARE = 5;

/**
 * Check if two line segments intersect
 * @param {number} x1
 * @param {number} y1
 * @param {number} x2
 * @param {number} y2
 * @param {number} x3
 * @param {number} y3
 * @param {number} x4
 * @param {number} y4
 * @returns {boolean}
 */
function lineSegmentsIntersect(x1, y1, x2, y2, x3, y3, x4, y4) {
  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (Math.abs(denom) < 0.0001) return false;

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

  return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
}

/**
 * Check if three points are collinear
 * @param {number} x1
 * @param {number} y1
 * @param {number} x2
 * @param {number} y2
 * @param {number} x3
 * @param {number} y3
 * @returns {boolean}
 */
function arePointsCollinear(x1, y1, x2, y2, x3, y3) {
  const area = Math.abs((x2 - x1) * (y3 - y1) - (x3 - x1) * (y2 - y1));
  return area < 1;
}

/**
 * Check if two collinear segments overlap
 * @param {number} x1
 * @param {number} y1
 * @param {number} x2
 * @param {number} y2
 * @param {number} x3
 * @param {number} y3
 * @param {number} x4
 * @param {number} y4
 * @returns {boolean}
 */
function collinearSegmentsOverlap(x1, y1, x2, y2, x3, y3, x4, y4) {
  const minX1 = Math.min(x1, x2),
    maxX1 = Math.max(x1, x2);
  const minY1 = Math.min(y1, y2),
    maxY1 = Math.max(y1, y2);
  const minX2 = Math.min(x3, x4),
    maxX2 = Math.max(x3, x4);
  const minY2 = Math.min(y3, y4),
    maxY2 = Math.max(y3, y4);
  return !(maxX1 < minX2 || maxX2 < minX1 || maxY1 < minY2 || maxY2 < minY1);
}

/**
 * Get distance between two points
 * @param {{ x: number, y: number }} p1
 * @param {{ x: number, y: number }} p2
 * @returns {number}
 */
function distance(p1, p2) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Check if opening is on a wall segment
 * @param {{ x: number, y: number }} wallStart
 * @param {{ x: number, y: number }} wallEnd
 * @param {{ x: number, y: number }} openingStart
 * @param {{ x: number, y: number }} openingEnd
 * @returns {boolean}
 */
function isOpeningOnWallSegment(wallStart, wallEnd, openingStart, openingEnd) {
  if (
    !arePointsCollinear(wallStart.x, wallStart.y, wallEnd.x, wallEnd.y, openingStart.x, openingStart.y) ||
    !arePointsCollinear(wallStart.x, wallStart.y, wallEnd.x, wallEnd.y, openingEnd.x, openingEnd.y)
  ) {
    return false;
  }
  return collinearSegmentsOverlap(
    wallStart.x,
    wallStart.y,
    wallEnd.x,
    wallEnd.y,
    openingStart.x,
    openingStart.y,
    openingEnd.x,
    openingEnd.y,
  );
}

/**
 * Split a wall segment at an opening
 * @param {{ x: number, y: number }} wallStart
 * @param {{ x: number, y: number }} wallEnd
 * @param {{ x: number, y: number }} openingStart
 * @param {{ x: number, y: number }} openingEnd
 * @returns {Array<{ start: { x: number, y: number }, end: { x: number, y: number } }>}
 */
function splitWallSegmentAtOpening(wallStart, wallEnd, openingStart, openingEnd) {
  const segments = [];
  if (distance(wallStart, openingStart) < distance(wallStart, openingEnd)) {
    if (distance(wallStart, openingStart) > 0.1) {
      segments.push({ start: wallStart, end: openingStart });
    }
    if (distance(openingEnd, wallEnd) > 0.1) {
      segments.push({ start: openingEnd, end: wallEnd });
    }
  } else {
    if (distance(wallStart, openingEnd) > 0.1) {
      segments.push({ start: wallStart, end: openingEnd });
    }
    if (distance(openingStart, wallEnd) > 0.1) {
      segments.push({ start: openingStart, end: wallEnd });
    }
  }
  return segments;
}

/**
 * Get effective wall segments with openings cut out (for movement collision)
 * @param {Array<Wall>} walls
 * @param {Array<Opening>} openings
 * @param {number} gridSize
 * @returns {Array<{ start: { x: number, y: number }, end: { x: number, y: number } }>}
 */
function getEffectiveWallSegments(walls, openings, gridSize) {
  let effectiveWalls = [];

  for (const wall of walls) {
    if (!wall.points || wall.points.length < 2) continue;
    for (let i = 0; i < wall.points.length - 1; i++) {
      const start = wall.points[i];
      const end = wall.points[i + 1];
      if (!start || !end) continue;
      effectiveWalls.push({
        start: gridPointToPixel(start, gridSize),
        end: gridPointToPixel(end, gridSize),
      });
    }
  }

  for (const opening of openings) {
    if (!opening.isComplete || !opening.points || opening.points.length < 2) continue;
    for (let i = 0; i < opening.points.length - 1; i++) {
      const openingPointStart = opening.points[i];
      const openingPointEnd = opening.points[i + 1];
      if (!openingPointStart || !openingPointEnd) continue;
      const openingStart = gridPointToPixel(openingPointStart, gridSize);
      const openingEnd = gridPointToPixel(openingPointEnd, gridSize);

      /** @type {Array<{ start: { x: number, y: number }, end: { x: number, y: number } }>} */
      const newEffectiveWalls = [];
      for (const wallSegment of effectiveWalls) {
        if (isOpeningOnWallSegment(wallSegment.start, wallSegment.end, openingStart, openingEnd)) {
          const splitSegments = splitWallSegmentAtOpening(wallSegment.start, wallSegment.end, openingStart, openingEnd);
          newEffectiveWalls.push(...splitSegments);
        } else {
          newEffectiveWalls.push(wallSegment);
        }
      }
      effectiveWalls = newEffectiveWalls;
    }
  }

  return effectiveWalls;
}

/**
 * Determines the token direction based on the movement vector
 * @param {{ x: number, y: number }} from
 * @param {{ x: number, y: number }} to
 * @returns {import('../types/jsdoc-types.js').TokenDirection}
 */
export function getDirectionFromMovement(from, to) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);

  // Diagonal when both axes have similar magnitude
  if (adx > 0 && ady > 0 && adx * 2 >= ady && ady * 2 >= adx) {
    if (dx > 0 && dy > 0) return 'down-right';
    if (dx > 0 && dy < 0) return 'up-right';
    if (dx < 0 && dy > 0) return 'down-left';
    return 'up-left';
  }

  if (adx > ady) {
    return dx > 0 ? 'right' : 'left';
  }
  return dy > 0 ? 'down' : 'up';
}

/**
 * Checks if a movement path crosses any walls or blocked openings.
 * For diagonal movements, also checks both adjacent orthogonal paths to prevent corner-cutting.
 * @param {{ x: number, y: number }} from - Pixel center of source cell
 * @param {{ x: number, y: number }} to - Pixel center of destination cell
 * @param {Array<Wall>} walls
 * @param {Array<Opening>} openings
 * @param {number} gridSize
 * @returns {boolean}
 */
function doesMovementCrossWall(from, to, walls, openings, gridSize) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const isDiagonal = dx !== 0 && dy !== 0;

  if (isDiagonal) {
    const intermediate1 = { x: from.x + dx, y: from.y };
    const intermediate2 = { x: from.x, y: from.y + dy };

    if (
      doesMovementCrossWall(from, intermediate1, walls, openings, gridSize) ||
      doesMovementCrossWall(from, intermediate2, walls, openings, gridSize)
    ) {
      return true;
    }
  }

  const effectiveWalls = getEffectiveWallSegments(walls, openings, gridSize);

  for (const wallSegment of effectiveWalls) {
    if (
      lineSegmentsIntersect(
        from.x,
        from.y,
        to.x,
        to.y,
        wallSegment.start.x,
        wallSegment.start.y,
        wallSegment.end.x,
        wallSegment.end.y,
      )
    ) {
      return true;
    }
  }

  for (const opening of openings) {
    if (!opening.blocksMovement) continue;
    if (opening.type === 'door' && opening.isOpen !== false) continue;
    if (!opening.points || opening.points.length < 2) continue;

    for (let i = 0; i < opening.points.length - 1; i++) {
      const openingPointStart = opening.points[i];
      const openingPointEnd = opening.points[i + 1];
      if (!openingPointStart || !openingPointEnd) continue;
      const openingStart = gridPointToPixel(openingPointStart, gridSize);
      const openingEnd = gridPointToPixel(openingPointEnd, gridSize);

      if (
        lineSegmentsIntersect(from.x, from.y, to.x, to.y, openingStart.x, openingStart.y, openingEnd.x, openingEnd.y)
      ) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Build a movement trail by attempting to add a new position.
 * Handles backtracking, wall collision, token occupancy, visibility, and movement limits.
 *
 * @param {object} params
 * @param {Array<{ x: number, y: number }>} params.trail - Current trail (pixel positions, cell centers)
 * @param {{ x: number, y: number }} params.newPos - New candidate position (pixel center)
 * @param {Array<Wall>} params.walls
 * @param {Array<Opening>} params.openings
 * @param {Array<Token>} params.tokens
 * @param {string} params.draggedTokenId
 * @param {number} params.gridSize
 * @param {number} params.maxMovementFeet
 * @param {Array<{col: number, row: number}>|undefined} params.visibleCells
 * @returns {{ direction: string|null, trail: Array<{ x: number, y: number }> }|null}
 */
export function buildMovementTrail({
  trail,
  newPos,
  walls,
  openings,
  tokens,
  draggedTokenId,
  gridSize,
  maxMovementFeet,
  visibleCells,
}) {
  if (trail.length === 0) return null;

  const lastPos = trail[trail.length - 1];
  if (!lastPos) return null;

  // Check if position actually changed
  if (lastPos.x === newPos.x && lastPos.y === newPos.y) return null;

  // Check if new position is adjacent (one grid step away max)
  const colDiff = Math.abs(Math.round((newPos.x - lastPos.x) / gridSize));
  const rowDiff = Math.abs(Math.round((newPos.y - lastPos.y) / gridSize));
  if (colDiff > 1 || rowDiff > 1 || (colDiff === 0 && rowDiff === 0)) return null;

  // Check for backtracking
  const existingIndex = trail.findIndex((pos) => pos.x === newPos.x && pos.y === newPos.y);
  if (existingIndex !== -1 && existingIndex < trail.length - 1) {
    const truncated = trail.slice(0, existingIndex + 1);
    let direction = null;
    if (truncated.length >= 2) {
      const secondToLast = truncated[truncated.length - 2];
      if (secondToLast) {
        direction = getDirectionFromMovement(secondToLast, newPos);
      }
    }
    return { direction, trail: truncated };
  }

  // Check movement limit
  const maxTrailLength = maxMovementFeet ? Math.floor(maxMovementFeet / FEET_PER_GRID_SQUARE) + 1 : 7;
  if (trail.length >= maxTrailLength) return null;

  // Check wall collision
  if (doesMovementCrossWall(lastPos, newPos, walls, openings, gridSize)) return null;

  // Check token occupancy — allow moving through allies (PCs) and defeated
  // monsters per 5e rules. Only block on living enemy monsters.
  const destCol = Math.floor(newPos.x / gridSize);
  const destRow = Math.floor(newPos.y / gridSize);
  const blockedByEnemy = tokens.some(
    (t) => t.id !== draggedTokenId && t.col === destCol && t.row === destRow && t._kind === 'monster' && !t.defeated,
  );
  if (blockedByEnemy) return null;

  // Check visibility
  if (visibleCells) {
    const isVisible = visibleCells.some((c) => c.col === destCol && c.row === destRow);
    if (!isVisible) return null;
  }

  // Valid move - add to trail
  const direction = getDirectionFromMovement(lastPos, newPos);
  return { direction, trail: [...trail, newPos] };
}
